import React from "react";

import localForage from "localforage";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";
import { RawIntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { BrowserRouter, RouterProvider } from "react-router-dom";

import AppErrorBoundary from "Components/AppErrorBoundary";
import config from "console_config";
import { ANALYTICS_ENABLED } from "Constants/constants";
import { getIntl } from "Libs/intl";
import { identifyPendo } from "Store/analytics/pendoTracker";

import { setTracking } from "./root/pages/utils/cookie";
import authRoutes from "./routes.auth";
import consoleRoutes from "./routes.console";
import configureAppStore from "./store/configureStore";

import("Vendor_resources/images/favicon.ico");

// Conduct a hard refresh of the page if one of the assets bundled by Vite
// fails to load. This is needed in the event a cached version of page is currently
// loaded but the assets are no longer available because of a rebuild+deployment.
window.addEventListener("vite:preloadError", async event => {
  const hasRefreshed = await localForage.getItem("hasRefreshedAfterError");
  if (hasRefreshed) {
    await localForage.removeItem("hasRefreshedAfterError");
    return;
  }
  await localForage.setItem("hasRefreshedAfterError", true);
  window.location.reload(true);
});

// If on Auth UI, store the tracking on a singleton
// to forward it in the request to the Auth Server
if (window.location.origin === config.URL_AUTH) {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach((v, k) => {
    if (k !== "_gclid" && !k.startsWith("_utm_") && k !== "trial_type") {
      urlParams.delete(k);
    }
  });

  setTracking(urlParams);
}

if (ANALYTICS_ENABLED) {
  const tagManagerArgs = {
    gtmId: config.ID_GOOGLE_TAG_MANAGER,
    cookieDomain: config.CUSTOM_COOKIE_DOMAIN
  };
  TagManager.initialize(tagManagerArgs);
}

const store = configureAppStore();
const isAuth = window.location.origin === config.URL_AUTH;
const appContainer = document.getElementById("app");
const intl = getIntl();
const fallbackContainer = document.getElementById("fallback");
const contactLink = document.querySelector("a.main-error-cta");

setTimeout(() => {
  if (fallbackContainer && appContainer.hasChildNodes()) {
    fallbackContainer.parentNode
      ? fallbackContainer.parentNode.removeChild(fallbackContainer)
      : "";
  }
  const pageErrorCtaLabel = intl.messages["error.main_page_cta_label"];
  if (contactLink && pageErrorCtaLabel) {
    contactLink.textContent = pageErrorCtaLabel;
  }
}, 10000);

if (config.ID_PLATFORM_TREE) {
  //  eslint-disable-next-line no-console
  console.log(`Build ID: %c${config.ID_PLATFORM_TREE}`, "color: green");
}

const root = createRoot(appContainer);
const shouldLoadStripe = !isAuth && config.ENABLE_STRIPE;
const shouldLoadPendo = isAuth;

if (shouldLoadPendo) {
  identifyPendo({});
}

if (shouldLoadStripe) {
  const script = document.createElement("script");
  script.src = "https://js.stripe.com/v3/";
  document.body.appendChild(script);
}

root.render(
  <AppErrorBoundary intl={intl}>
    <Provider store={store}>
      <RawIntlProvider value={intl}>
        <React.Suspense fallback={<div />}>
          <RouterProvider
            router={isAuth ? authRoutes : consoleRoutes}
            fallbackElement={<div />}
          />
        </React.Suspense>
      </RawIntlProvider>
    </Provider>
  </AppErrorBoundary>
);
